import { DestroyRef, ErrorHandler, Injectable, Injector, inject } from '@angular/core';
import { NavigationOutcomeService } from './navigation-outcome.service';

/**
 * Both engines report a lazy chunk that did not load through the same failure, worded
 * differently: Firefox says "error loading dynamically imported module", Chromium says
 * "Failed to fetch dynamically imported module".
 */
const DYNAMIC_IMPORT_FAILURE = /dynamically imported module/i;

/**
 * Keeps a lazy chunk the app walked away from out of the error channel, and leaves
 * every other failure exactly where it was.
 *
 * Leaving a page while a route's chunk is still in flight aborts that request and
 * rejects the pending `import()`. Firefox surfaces the rejection; Chromium does not.
 * It reaches `provideBrowserGlobalErrorListeners` and the default handler prints it,
 * though nothing is wrong - a console error, in one engine only, for an answer nobody
 * is waiting for. Measured on the production build: Firefox 5/6, Chromium 0/6.
 *
 * The same message arrives when a chunk is genuinely gone, typically after a deploy
 * replaced the files an open tab still points at. That one matters - the route never
 * activates and the analyst stays on the previous screen - so it is left alone.
 *
 * Two things tell the harmless ones apart, and both had to be measured rather than
 * assumed:
 *
 * - **the document is being replaced.** The signal is `beforeunload`; in Firefox the
 *   rejection lands between it and `pagehide`, so `pagehide` is too late to be of use.
 * - **a newer navigation overtook it.** See {@link NavigationOutcomeService}.
 */
@Injectable()
export class LazyChunkErrorHandler extends ErrorHandler {
  // Resolved lazily: an ErrorHandler is built before the router, and asking for it
  // here would drag the router into the bootstrap graph.
  private readonly injector = inject(Injector);

  /**
   * The navigation id current when the browser announced it was leaving, or `null`
   * while the document is staying put.
   */
  private leavingAtNavigation: number | null = null;

  private readonly onBeforeUnload = (): void => {
    this.leavingAtNavigation = this.outcome()?.lastStartId() ?? 0;
  };

  /** Restored from the back/forward cache: the document that was leaving is back. */
  private readonly onPageShow = (): void => {
    this.leavingAtNavigation = null;
  };

  constructor() {
    super();
    addEventListener('beforeunload', this.onBeforeUnload);
    addEventListener('pageshow', this.onPageShow);

    // The app normally outlives these listeners, but not when it is torn down and
    // rebuilt in the same document: a leftover listener would reach for an injector
    // that no longer exists.
    inject(DestroyRef).onDestroy(() => {
      removeEventListener('beforeunload', this.onBeforeUnload);
      removeEventListener('pageshow', this.onPageShow);
    });
  }

  override handleError(error: unknown): void {
    if (this.isAbandonedChunkLoad(error)) {
      // Still recorded, just not as an application error: a burst of these points at
      // chunks arriving slower than the analyst moves.
      console.debug('[router] lazy chunk abandoned:', messageOf(error));
      return;
    }

    super.handleError(error);
  }

  private isAbandonedChunkLoad(error: unknown): boolean {
    if (!DYNAMIC_IMPORT_FAILURE.test(messageOf(error))) {
      return false;
    }

    if (this.documentIsLeaving()) {
      return true;
    }

    const outcome = this.outcome();
    // With no router to ask, the safe answer is to report: a swallowed failure is
    // worse than a noisy one.
    return outcome != null && !outcome.currentNavigationFailed();
  }

  /**
   * True while this document is on its way out. A navigation started after the
   * announcement means it stayed after all - a download link or an external protocol
   * raises `beforeunload` without replacing anything - so the flag clears itself
   * instead of silencing later failures for the rest of the session.
   */
  private documentIsLeaving(): boolean {
    if (this.leavingAtNavigation === null) {
      return false;
    }

    if ((this.outcome()?.lastStartId() ?? 0) > this.leavingAtNavigation) {
      this.leavingAtNavigation = null;
      return false;
    }

    return true;
  }

  private outcome(): NavigationOutcomeService | null {
    return this.injector.get(NavigationOutcomeService, null);
  }
}

function messageOf(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return typeof error === 'string'
    ? error
    : String((error as { message?: unknown })?.message ?? '');
}
